/* eslint-disable */
import React, { useState, useEffect, useCallback } from "react";
import "./Vendornotifypage.css";
const API_HOST =
  process.env.REACT_APP_API_URL ||
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────
const BASE_URL = API_HOST;
const API = `${BASE_URL}/api/notify`;

const STAGE_CONFIG = {
  order_received: { label: "Order Received", color: "#2563eb", bg: "#dbeafe" },
  qc_passed: { label: "QC Passed", color: "#059669", bg: "#d1fae5" },
  qc_failed: { label: "QC Failed", color: "#dc2626", bg: "#fee2e2" },
  delivered: { label: "Delivered", color: "#059669", bg: "#d1fae5" },
  cancelled: { label: "Cancelled", color: "#dc2626", bg: "#fee2e2" },
};

const STATUS_CONFIG = {
  sent: { label: "Sent", color: "#059669", bg: "#d1fae5" },
  skipped: { label: "Skipped", color: "#b45309", bg: "#fef3c7" },
  failed: { label: "Failed", color: "#dc2626", bg: "#fee2e2" },
};

const STAGE_MESSAGE = {
  order_received: "This will tell the vendor a new order has come in.",
  qc_passed:
    "This will tell the vendor their item passed QC and is on its way to the buyer.",
  qc_failed:
    "This will tell the vendor their item failed QC — add a reason below.",
  delivered: "This will confirm delivery and payout to the vendor.",
  cancelled:
    "This will tell the vendor the order was cancelled — add a reason below.",
};

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────
const Badge = ({ cfg, label }) => {
  if (!cfg) return <span className="vn-code">{label}</span>;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 12px",
        borderRadius: 20,
        fontSize: "0.78rem",
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.color}33`,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

const Spinner = ({ size = 16 }) => (
  <span
    style={{
      display: "inline-block",
      width: size,
      height: size,
      border: `2px solid #e5e7eb`,
      borderTop: `2px solid #2563eb`,
      borderRadius: "50%",
      animation: "vn-spin 0.7s linear infinite",
    }}
  />
);

const StatCard = ({ label, value, color }) => (
  <div className="vn-stat-card">
    <span className="vn-stat-value" style={color ? { color } : undefined}>
      {value ?? "—"}
    </span>
    <span className="vn-stat-label">{label}</span>
  </div>
);

// ─────────────────────────────────────────────────────────────
// DASHBOARD TAB — notification history, filters, retry
// (retry ab order_id + product_id + stage se hota hai — granularity
// ITEM-level hai, kyunke ek vendor ke 2 items bhi alag messages hain)
// ─────────────────────────────────────────────────────────────
const NotificationsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 1,
  });

  const [filters, setFilters] = useState({
    stage: "",
    status: "",
    order_id: "",
    vendor: "",
    date_from: "",
    date_to: "",
  });
  const [draft, setDraft] = useState({
    order_id: "",
    vendor: "",
    date_from: "",
    date_to: "",
  });

  const [retrying, setRetrying] = useState(null); // `${order_id}:${product_id}:${stage}`

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await fetch(`${API}/notifications/stats`);
      const json = await res.json();
      if (res.ok && json.success) setStats(json.data);
    } catch (err) {
      console.error(err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchNotifications = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set("page", page);
        params.set("limit", pagination.limit);
        if (filters.stage) params.set("stage", filters.stage);
        if (filters.status) params.set("status", filters.status);
        if (filters.order_id) params.set("order_id", filters.order_id);
        if (filters.vendor) params.set("vendor", filters.vendor);
        if (filters.date_from) params.set("date_from", filters.date_from);
        if (filters.date_to) params.set("date_to", filters.date_to);

        const res = await fetch(`${API}/notifications?${params.toString()}`);
        const json = await res.json();
        if (!res.ok || !json.success) {
          setError(json.error || "Failed to load notifications");
          setRows([]);
          return;
        }
        setRows(json.data);
        setPagination(json.pagination);
      } catch (err) {
        setError(err.message);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.limit],
  );

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchNotifications(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.stage,
    filters.status,
    filters.order_id,
    filters.vendor,
    filters.date_from,
    filters.date_to,
  ]);

  const applyTextFilters = () => {
    setFilters((f) => ({
      ...f,
      order_id: draft.order_id.trim(),
      vendor: draft.vendor.trim(),
      date_from: draft.date_from,
      date_to: draft.date_to,
    }));
  };

  const clearFilters = () => {
    setDraft({ order_id: "", vendor: "", date_from: "", date_to: "" });
    setFilters({
      stage: "",
      status: "",
      order_id: "",
      vendor: "",
      date_from: "",
      date_to: "",
    });
  };

  const goToPage = (p) => {
    if (p < 1 || p > pagination.total_pages) return;
    fetchNotifications(p);
  };

  const handleRetry = async (order_id, product_id, stage) => {
    const key = `${order_id}:${product_id}:${stage}`;
    setRetrying(key);
    try {
      const res = await fetch(
        `${API}/notify/retry/${order_id}/${product_id}/${stage}`,
        { method: "POST" },
      );
      const json = await res.json();
      if (!res.ok || !json.success) {
        alert(json.error || "Retry failed");
      }
      fetchNotifications(pagination.page);
      fetchStats();
    } catch (err) {
      alert(err.message);
    } finally {
      setRetrying(null);
    }
  };

  return (
    <div>
      {/* Stats */}
      <div className="vn-stats-row">
        <StatCard
          label="Total"
          value={statsLoading ? "…" : stats?.overall?.total}
        />
        <StatCard
          label="Sent"
          value={statsLoading ? "…" : stats?.overall?.sent}
          color="#059669"
        />
        <StatCard
          label="Skipped"
          value={statsLoading ? "…" : stats?.overall?.skipped}
          color="#b45309"
        />
        <StatCard
          label="Failed"
          value={statsLoading ? "…" : stats?.overall?.failed}
          color="#dc2626"
        />
      </div>

      {/* Filters */}
      <div className="vn-panel" style={{ marginBottom: 16 }}>
        <div className="vn-filters-grid">
          <div className="vn-field">
            <label className="vn-label">Stage</label>
            <select
              className="vn-input"
              value={filters.stage}
              onChange={(e) =>
                setFilters((f) => ({ ...f, stage: e.target.value }))
              }
            >
              <option value="">All stages</option>
              {Object.entries(STAGE_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>
                  {cfg.label}
                </option>
              ))}
            </select>
          </div>

          <div className="vn-field">
            <label className="vn-label">Status</label>
            <select
              className="vn-input"
              value={filters.status}
              onChange={(e) =>
                setFilters((f) => ({ ...f, status: e.target.value }))
              }
            >
              <option value="">All statuses</option>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>
                  {cfg.label}
                </option>
              ))}
            </select>
          </div>

          <div className="vn-field">
            <label className="vn-label">Order ID</label>
            <input
              type="text"
              className="vn-input"
              placeholder="e.g. 1042"
              value={draft.order_id}
              onChange={(e) =>
                setDraft((d) => ({ ...d, order_id: e.target.value }))
              }
              onKeyDown={(e) => e.key === "Enter" && applyTextFilters()}
            />
          </div>

          <div className="vn-field">
            <label className="vn-label">Vendor (name or phone)</label>
            <input
              type="text"
              className="vn-input"
              placeholder="e.g. Ali or +9230..."
              value={draft.vendor}
              onChange={(e) =>
                setDraft((d) => ({ ...d, vendor: e.target.value }))
              }
              onKeyDown={(e) => e.key === "Enter" && applyTextFilters()}
            />
          </div>

          <div className="vn-field">
            <label className="vn-label">From</label>
            <input
              type="date"
              className="vn-input"
              value={draft.date_from}
              onChange={(e) =>
                setDraft((d) => ({ ...d, date_from: e.target.value }))
              }
            />
          </div>

          <div className="vn-field">
            <label className="vn-label">To</label>
            <input
              type="date"
              className="vn-input"
              value={draft.date_to}
              onChange={(e) =>
                setDraft((d) => ({ ...d, date_to: e.target.value }))
              }
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button className="vn-btn-primary" onClick={applyTextFilters}>
            Apply Filters
          </button>
          <button className="vn-btn-sm" onClick={clearFilters}>
            Clear All
          </button>
        </div>
      </div>

      {/* Table / list */}
      <div className="vn-panel">
        {loading ? (
          <div className="vn-loading-center">
            <Spinner size={22} />
          </div>
        ) : error ? (
          <div className="vn-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="vn-empty">No notifications match these filters.</div>
        ) : (
          <div className="vn-activity-list">
            {rows.map((row) => {
              const key = `${row.order_id}:${row.product_id}:${row.stage}`;
              return (
                <div className="vn-activity-row" key={row.id}>
                  <div className="vn-activity-main">
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <span className="vn-order-id">
                        Order #{row.order_id}
                        {row.product_id ? (
                          <span
                            className="vn-code"
                            style={{ marginLeft: 6 }}
                            title="Item-level notification — this order may have other items too"
                          >
                            item #{row.product_id}
                          </span>
                        ) : null}
                      </span>
                      {row.marketplace && (
                        <span className="vn-code">{row.marketplace}</span>
                      )}
                      <Badge
                        cfg={STAGE_CONFIG[row.stage]}
                        label={STAGE_CONFIG[row.stage]?.label || row.stage}
                      />
                      <Badge
                        cfg={STATUS_CONFIG[row.status]}
                        label={STATUS_CONFIG[row.status]?.label || row.status}
                      />
                    </div>
                    <span className="vn-activity-time">
                      {row.sent_at
                        ? new Date(row.sent_at).toLocaleString()
                        : "—"}
                    </span>
                  </div>

                  <div className="vn-details-grid" style={{ marginTop: 4 }}>
                    <div className="vn-detail">
                      <span className="vn-detail-label">Vendor</span>
                      <span className="vn-detail-value">
                        {row.vendor_name || "—"}
                      </span>
                    </div>
                    <div className="vn-detail">
                      <span className="vn-detail-label">Phone</span>
                      <span className="vn-detail-value">
                        {row.vendor_phone || "—"}
                      </span>
                    </div>
                    <div className="vn-detail">
                      <span className="vn-detail-label">Item</span>
                      <span className="vn-detail-value">
                        {row.product_name || "—"}
                      </span>
                    </div>
                    <div className="vn-detail">
                      <span className="vn-detail-label">Item Price</span>
                      <span className="vn-detail-value">
                        {row.display_item_total ?? "—"}
                      </span>
                    </div>
                  </div>

                  {row.status === "sent" && row.twilio_sid && (
                    <span className="vn-activity-sid">
                      <span className="vn-code">SID: {row.twilio_sid}</span>
                    </span>
                  )}

                  {row.status === "failed" && (
                    <>
                      {row.error_message && (
                        <span className="vn-activity-reason">
                          ⚠ {row.error_message}
                        </span>
                      )}
                      <button
                        className="vn-btn-sm"
                        style={{ alignSelf: "flex-start" }}
                        disabled={retrying === key}
                        onClick={() =>
                          handleRetry(row.order_id, row.product_id, row.stage)
                        }
                      >
                        {retrying === key ? (
                          <>
                            <Spinner size={12} /> Retrying…
                          </>
                        ) : (
                          "Retry Send"
                        )}
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 8,
              marginTop: 16,
              alignItems: "center",
            }}
          >
            <button
              className="vn-btn-sm"
              disabled={pagination.page <= 1}
              onClick={() => goToPage(pagination.page - 1)}
            >
              ← Prev
            </button>
            <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>
              Page {pagination.page} of {pagination.total_pages} (
              {pagination.total} total)
            </span>
            <button
              className="vn-btn-sm"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => goToPage(pagination.page + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MANUAL SEND TAB — order fetch ab items array return karta hai
// (per ITEM, na ke per vendor). Agar order mein 1 se zyada item
// hai (chahe same vendor ke hi kyun na hon), pehle item choose
// karna padta hai — kyunke har item ka apna alag message jaata hai.
// ─────────────────────────────────────────────────────────────
const ManualSend = () => {
  const [orderId, setOrderId] = useState("");
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [orderMeta, setOrderMeta] = useState(null); // { order_id, marketplace_order_id, currency }
  const [itemEntries, setItemEntries] = useState([]); // array from API
  const [selectedProductId, setSelectedProductId] = useState(null);

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [payoutOverride, setPayoutOverride] = useState("");
  const [qcReason, setQcReason] = useState("");
  const [cancelReason, setCancelReason] = useState("");

  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const reset = () => {
    setOrderMeta(null);
    setItemEntries([]);
    setSelectedProductId(null);
    setSelectedTemplate(null);
    setPayoutOverride("");
    setQcReason("");
    setCancelReason("");
    setSendResult(null);
  };

  const handleFetch = async () => {
    if (!orderId.trim()) return;
    reset();
    setFetchError(null);
    setFetching(true);
    try {
      const res = await fetch(`${API}/order/${orderId.trim()}`);
      const json = await res.json();
      if (!res.ok) {
        setFetchError(json.error || "Order not found");
        return;
      }
      setOrderMeta({
        order_id: json.order_id,
        marketplace_order_id: json.marketplace_order_id,
        currency: json.currency,
      });
      setItemEntries(json.items || []);
      if (json.items && json.items.length === 1) {
        const only = json.items[0];
        setSelectedProductId(only.product_id);
        setSelectedTemplate(only.next_notification);
      }
      // agar 1 se zyada items hain, user ko dropdown se choose karna hoga
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setFetching(false);
    }
  };

  const selectedItem = itemEntries.find(
    (it) => it.product_id === selectedProductId,
  );

  const handlePickItem = (productId) => {
    const entry = itemEntries.find((it) => it.product_id === productId);
    setSelectedProductId(productId);
    setSelectedTemplate(entry ? entry.next_notification : null);
    setPayoutOverride("");
    setQcReason("");
    setCancelReason("");
    setSendResult(null);
  };

  const handleSend = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch(`${API}/notify/test-send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_id: orderMeta.order_id,
          product_id: selectedProductId,
          template: selectedTemplate,
          payout_amount: payoutOverride || undefined,
          qc_failure_reason: qcReason || undefined,
          cancellation_reason: cancelReason || undefined,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        setSendResult({ ok: false, message: json.error || "Send failed" });
        return;
      }
      if (json.skipped) {
        setSendResult({ ok: false, message: `Skipped — ${json.reason}` });
      } else {
        setSendResult({
          ok: true,
          message: `Sent "${STAGE_CONFIG[selectedTemplate]?.label}" template for item #${selectedProductId} (SID: ${json.sid})`,
        });
      }
    } catch (err) {
      setSendResult({ ok: false, message: err.message });
    } finally {
      setSending(false);
    }
  };

  const detectedStage = selectedItem?.next_notification;
  const isOverridden = selectedTemplate !== detectedStage;

  return (
    <div className="vn-panel vn-single-card">
      <div className="vn-fetch-row">
        <input
          type="text"
          className="vn-input"
          placeholder="Enter Order ID (e.g. 1042)"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleFetch()}
        />
        <button
          className="vn-btn-primary"
          onClick={handleFetch}
          disabled={fetching}
        >
          {fetching ? (
            <>
              <Spinner size={14} /> Fetching…
            </>
          ) : (
            "Fetch Order"
          )}
        </button>
      </div>

      {fetchError && <div className="vn-error">{fetchError}</div>}

      {/* Multiple items -> pehle choose karna hai */}
      {orderMeta && itemEntries.length > 1 && !selectedProductId && (
        <div className="vn-details-card">
          <p className="vn-stage-hint" style={{ marginBottom: 12 }}>
            ⚠ Is order mein <strong>{itemEntries.length} items</strong> hain —
            har item ka apna alag message jaata hai. Pehle batao kis item ke
            liye bhejna hai:
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {itemEntries.map((it) => (
              <button
                key={it.product_id}
                className="vn-btn-sm"
                style={{ textAlign: "left", padding: "10px 14px" }}
                onClick={() => handlePickItem(it.product_id)}
              >
                <strong>{it.title}</strong> — {it.vendor.name} (item #
                {it.product_id}) — {orderMeta.currency} {it.price}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Item selected (ya sirf 1 hi tha) -> details + send form */}
      {orderMeta && selectedItem && (
        <>
          {itemEntries.length > 1 && (
            <div style={{ marginBottom: 8 }}>
              <button
                className="vn-btn-sm"
                onClick={() => {
                  setSelectedProductId(null);
                  setSelectedTemplate(null);
                }}
              >
                ← Doosra item choose karo
              </button>
            </div>
          )}

          <div className="vn-details-card">
            <div className="vn-details-top">
              <Badge
                cfg={STAGE_CONFIG[detectedStage]}
                label={`Actual: ${STAGE_CONFIG[detectedStage]?.label || detectedStage}`}
              />
              <span className="vn-order-id">
                Order #{orderMeta.order_id} — item #{selectedProductId}
              </span>
            </div>

            <div className="vn-details-grid">
              <div className="vn-detail">
                <span className="vn-detail-label">Vendor</span>
                <span className="vn-detail-value">
                  {selectedItem.vendor.name}
                </span>
              </div>
              <div className="vn-detail">
                <span className="vn-detail-label">Phone</span>
                <span className="vn-detail-value">
                  {selectedItem.vendor.phone_number}
                </span>
              </div>
              <div className="vn-detail">
                <span className="vn-detail-label">Item</span>
                <span className="vn-detail-value">
                  {selectedItem.title || "—"}
                </span>
              </div>
              <div className="vn-detail">
                <span className="vn-detail-label">Item price</span>
                <span className="vn-detail-value">
                  {orderMeta.currency || ""} {selectedItem.price}
                </span>
              </div>
              <div className="vn-detail">
                <span className="vn-detail-label">QC status</span>
                <span className="vn-detail-value">
                  {selectedItem.qc_status}
                </span>
              </div>
              <div className="vn-detail">
                <span className="vn-detail-label">Order status</span>
                <span className="vn-detail-value">
                  {selectedItem.cancelled_status === "cancelled"
                    ? "Cancelled"
                    : selectedItem.delivered_status === "delivered"
                      ? "Delivered"
                      : "In progress"}
                </span>
              </div>
            </div>

            <div className="vn-field" style={{ marginTop: 4 }}>
              <label className="vn-label">
                Template to send{" "}
                {isOverridden && (
                  <span style={{ color: "#b45309", fontWeight: 700 }}>
                    (overridden — for testing only)
                  </span>
                )}
              </label>
              <select
                className="vn-input"
                value={selectedTemplate || ""}
                onChange={(e) => setSelectedTemplate(e.target.value)}
              >
                {Object.entries(STAGE_CONFIG).map(([key, cfg]) => (
                  <option key={key} value={key}>
                    {cfg.label}
                    {key === detectedStage ? " (actual)" : ""}
                  </option>
                ))}
              </select>
            </div>

            <p className="vn-stage-hint">{STAGE_MESSAGE[selectedTemplate]}</p>

            {(selectedTemplate === "order_received" ||
              selectedTemplate === "qc_passed" ||
              selectedTemplate === "delivered") && (
              <div className="vn-field">
                <label className="vn-label">
                  Payout amount (optional override)
                </label>
                <input
                  type="text"
                  className="vn-input"
                  placeholder="defaults to computed seller payout"
                  value={payoutOverride}
                  onChange={(e) => setPayoutOverride(e.target.value)}
                />
              </div>
            )}
            {selectedTemplate === "qc_failed" && (
              <div className="vn-field">
                <label className="vn-label">QC failure reason</label>
                <input
                  type="text"
                  className="vn-input"
                  placeholder="e.g. Item damaged"
                  value={qcReason}
                  onChange={(e) => setQcReason(e.target.value)}
                />
              </div>
            )}
            {selectedTemplate === "cancelled" && (
              <div className="vn-field">
                <label className="vn-label">Cancellation reason</label>
                <input
                  type="text"
                  className="vn-input"
                  placeholder="e.g. Buyer cancelled"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                />
              </div>
            )}
          </div>

          {sendResult && (
            <div className={sendResult.ok ? "vn-success" : "vn-error"}>
              {sendResult.message}
            </div>
          )}

          <button
            className="vn-btn-send"
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? (
              <>
                <Spinner size={16} /> Sending…
              </>
            ) : (
              `Send "${STAGE_CONFIG[selectedTemplate]?.label || ""}" Message`
            )}
          </button>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────
const VendorNotifyPage = () => {
  const [activeTab, setActiveTab] = useState("dashboard");

  return (
    <div className="vn-page">
      <div className="vn-header">
        <div>
          <h1 className="vn-page-title">Vendor Notifications</h1>
          <p className="vn-page-sub">
            {activeTab === "dashboard"
              ? "Every WhatsApp notification sent, skipped, or failed — automatically."
              : "Manually look up an order and send its current-stage notification."}
          </p>
        </div>
      </div>

      <div className="vn-tabs">
        <button
          className={`vn-tab ${activeTab === "dashboard" ? "active" : ""}`}
          onClick={() => setActiveTab("dashboard")}
        >
          Dashboard
        </button>
        <button
          className={`vn-tab ${activeTab === "manual" ? "active" : ""}`}
          onClick={() => setActiveTab("manual")}
        >
          Manual Send
        </button>
      </div>

      {activeTab === "dashboard" ? <NotificationsDashboard /> : <ManualSend />}
    </div>
  );
};

export default VendorNotifyPage;
